import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Image,
  TouchableOpacity,
  Platform,
  Alert,
} from "react-native";
import * as Location from "expo-location";
import { useNavigation } from "@react-navigation/native";
import { FontAwesome } from "@expo/vector-icons";
import { RootStackParamList } from "../App";
import { StackNavigationProp } from "@react-navigation/stack";
import { useFavorites } from "../contexts/FavoritesContext"; // Import useFavorites

const BuyerHomeScreen = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { favorites, toggleFavorite } = useFavorites(); // Get favorites from context
  const [location, setLocation] = useState<string>("Fetching location...");

  const propertyData = [
    {
      id: "1",
      title: "2 Niveaux Maison Modern",
      location: "Grand-Bassam, Abidjan, Cote D'ivoire",
      price: "40,000,000 Franc CFA",
      image: require("../assets/default/property_sample1.png"),
    },
    {
      id: "2",
      title: "Maison Famille",
      location: "Deux-Plateaux, Abidjan, Cote D'ivoire",
      price: "15,000,000 Franc CFA",
      image: require("../assets/default/property_sample2.png"),
    },
  ];

  useEffect(() => {
    (async () => {
      if (Platform.OS === "web") {
        if ("geolocation" in navigator) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              try {
                const response = await fetch(
                  `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
                );
                const data = await response.json();
                if (data?.address) {
                  const { city, town, village, country } = data.address;
                  setLocation(`${city || town || village || "Unknown City"}, ${country || "Unknown Country"}`);
                } else {
                  setLocation("Location not found");
                }
              } catch {
                setLocation("Error fetching location");
              }
            },
            () => setLocation("Location not available")
          );
        } else {
          setLocation("Geolocation not supported");
        }
      } else {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== "granted") {
          Alert.alert("Permission denied", "Location permission is required.");
          setLocation("Permission denied");
          return;
        }

        const userLocation = await Location.getCurrentPositionAsync({});
        const { latitude, longitude } = userLocation.coords;

        try {
          const [reverseGeocode] = await Location.reverseGeocodeAsync({ latitude, longitude });
          if (reverseGeocode) {
            setLocation(`${reverseGeocode.city || "Unknown city"}, ${reverseGeocode.region || "Unknown state"}`);
          } else {
            setLocation("Unable to fetch location");
          }
        } catch {
          setLocation("Error fetching location");
        }
      }
    })();
  }, []);

  const renderPropertyCard = ({ item }: { item: any }) => {
    const isFavorited = favorites.includes(item.id);
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate("PropertyDetailScreen", { property: item })}
      >
        <Image source={item.image} style={styles.cardImage} />
        <View style={styles.cardDetails}>
          <Text style={styles.cardTitle}>{item.title}</Text>
          <Text style={styles.cardSubtitle}>{item.location}</Text>
          <Text style={styles.cardPrice}>{item.price}</Text>
        </View>
        <TouchableOpacity
          style={styles.favoriteButton}
          onPress={() => toggleFavorite(item.id)}
        >
          <FontAwesome name={isFavorited ? "heart" : "heart-o"} size={20} color={isFavorited ? "red" : "#ffffff"} />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.greetingSection}>
        <Text style={styles.greetingText}>Good morning</Text>
        <Text style={styles.locationText}>{location}</Text>
        <TouchableOpacity style={styles.avatarButton}>
          <FontAwesome name="bell" size={24} color="#000" />
        </TouchableOpacity>
      </View>

      <FlatList
        data={propertyData}
        keyExtractor={(item) => item.id}
        renderItem={renderPropertyCard}
        contentContainerStyle={styles.propertyList}
      />

      <View style={styles.bottomNavigation}>
        <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate("BuyerHomeScreen")}>
          <FontAwesome name="home" size={24} color="#007BFF" />
          <Text style={styles.navText}>Home</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate("MapScreen")}>
          <FontAwesome name="map" size={24} color="#aaa" />
          <Text style={styles.navText}>Maps</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate("ListingsScreen")}>
          <FontAwesome name="list" size={24} color="#aaa" />
          <Text style={styles.navText}>Listings</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate("BuyerProfileScreen")}>
          <FontAwesome name="user" size={24} color="#aaa" />
          <Text style={styles.navText}>Profile</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F8F8F8", paddingTop: 40 },
  greetingSection: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 20, marginBottom: 20 },
  greetingText: { fontSize: 24, fontWeight: "bold", color: "#333" },
  locationText: { fontSize: 16, color: "#555", marginTop: 5 },
  avatarButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: "#E0E0E0", justifyContent: "center", alignItems: "center" },
  propertyList: { paddingHorizontal: 20 },
  card: { backgroundColor: "#ffffff", borderRadius: 10, marginBottom: 20, overflow: "hidden", elevation: 3 },
  cardImage: { width: "100%", height: 150 },
  cardDetails: { padding: 10 },
  cardTitle: { fontSize: 16, fontWeight: "bold", color: "#333" },
  cardSubtitle: { fontSize: 14, color: "#666", marginVertical: 5 },
  cardPrice: { fontSize: 14, color: "#007BFF", fontWeight: "bold" },
  favoriteButton: { position: "absolute", top: 10, right: 10, backgroundColor: "rgba(0,0,0,0.5)", padding: 8, borderRadius: 15 },
  bottomNavigation: { flexDirection: "row", justifyContent: "space-around", backgroundColor: "#ffffff", paddingVertical: 10, borderTopWidth: 1, borderTopColor: "#E0E0E0" },
  navItem: { alignItems: "center" },
  navText: { fontSize: 12, color: "#333", marginTop: 5 },
});

export default BuyerHomeScreen;
